import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        alert('Nur Administratoren haben Zugriff auf die Benutzerverwaltung');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, formData);
      } else {
        if (!formData.password) {
          alert('Passwort ist erforderlich für neue Benutzer');
          return;
        }
        await axios.post('/api/users', formData);
      }
      fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleDelete = (user) => {
    setConfirmDialog({
      show: true,
      title: 'Benutzer löschen',
      message: `Möchten Sie "${user.username}" wirklich löschen?`,
      onConfirm: async () => {
        try {
          await axios.delete(`/api/users/${user.id}`);
          fetchUsers();
          setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          console.error('Error deleting user:', error);
          alert(error.response?.data?.error || 'Fehler beim Löschen');
        }
      }
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      is_active: true
    });
  };

  const getRoleBadge = (role) => {
    return role === 'admin' 
      ? { bg: '#fee2e2', color: '#991b1b', text: 'Administrator' }
      : { bg: '#dbeafe', color: '#1e40af', text: 'Benutzer' };
  };

  if (loading) {
    return <div className="loading">Lade Benutzer...</div>;
  }

  return (
    <>
      <style>
        {`
          @keyframes buttonShimmer {
            0% {
              transform: translateX(-100%) translateY(-100%) rotate(45deg);
            }
            100% {
              transform: translateX(100%) translateY(100%) rotate(45deg);
            }
          }

          .button-primary {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border: 2px solid rgba(255,255,255,0.2);
            padding: 0.75rem 1.5rem;
            borderRadius: 12px;
            cursor: pointer;
            fontSize: 0.95rem;
            fontWeight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            boxShadow: 0 8px 16px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255,255,255,0.2);
            position: relative;
            overflow: hidden;
          }

          .button-primary::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }

          .button-primary:hover::before {
            width: 300px;
            height: 300px;
          }

          .button-primary:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 15px 35px rgba(59, 130, 246, 0.5), 0 0 30px rgba(59, 130, 246, 0.3);
          }

          .button-primary:active {
            transform: translateY(-1px) scale(1.02);
          }

          .action-button {
            border: 2px solid rgba(255,255,255,0.3);
            padding: 0.5rem 0.8rem;
            border-radius: 10px;
            cursor: pointer;
            fontSize: 0.75rem;
            font-weight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 32px;
          }

          .action-button::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent);
            animation: buttonShimmer 3s infinite;
          }

          .action-button:hover {
            transform: translateY(-3px) scale(1.08);
          }

          .action-button:active {
            transform: translateY(-1px) scale(1.03);
          }

          .action-button-blue {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
          }

          .action-button-blue:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            box-shadow: 0 12px 30px rgba(59, 130, 246, 0.5), 0 0 25px rgba(59, 130, 246, 0.4);
          }

          .action-button-red {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);
          }

          .action-button-red:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            box-shadow: 0 12px 30px rgba(239, 68, 68, 0.5), 0 0 25px rgba(239, 68, 68, 0.4);
          }

          .cancel-button {
            background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
            color: #475569;
            border: 2px solid #e2e8f0;
            padding: 0.75rem 1.5rem;
            border-radius: 10px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
          }

          .cancel-button::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.05);
            transform: translate(-50%, -50%);
            transition: width 0.5s, height 0.5s;
          }

          .cancel-button:hover::before {
            width: 200px;
            height: 200px;
          }

          .cancel-button:hover {
            background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
            transform: translateY(-2px) scale(1.03);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }

          .table-row {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .table-row:hover {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            transform: translateX(4px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          }

          .status-badge {
            transition: all 0.3s ease;
            display: inline-block;
          }

          .status-badge:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .custom-checkbox {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            accent-color: #3b82f6;
          }

          .custom-checkbox:hover {
            transform: scale(1.15);
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
          }
        `}
      </style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{ color: '#1e293b', fontSize: '2rem', marginBottom: '0.5rem' }}>
              Benutzerverwaltung
            </h1>
            <p style={{ color: '#64748b' }}>
              Verwalten Sie Benutzer und deren Berechtigungen
            </p>
          </div>
          <button onClick={() => setShowModal(true)} className="button-primary">
            Neuer Benutzer
          </button>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#475569' }}>
                  Benutzername
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#475569' }}>
                  E-Mail
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#475569' }}>
                  Rolle
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#475569' }}>
                  Status
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#475569' }}>
                  Letzter Login
                </th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#475569' }}>
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const roleBadge = getRoleBadge(user.role);
                return (
                  <tr key={user.id} className="table-row" style={{
                    borderBottom: index < users.length - 1 ? '1px solid #f1f5f9' : 'none'
                  }}>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>{user.username}</div>
                    </td>
                    <td style={{ padding: '1rem', color: '#64748b' }}>
                      {user.email || '-'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className="status-badge" style={{
                        background: roleBadge.bg,
                        color: roleBadge.color,
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {roleBadge.text}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className="status-badge" style={{
                        background: user.is_active ? '#d1fae5' : '#fee2e2',
                        color: user.is_active ? '#065f46' : '#991b1b',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {user.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: '#64748b', fontSize: '0.875rem' }}>
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleString('de-DE')
                        : 'Noch nie'
                      }
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleEdit(user)}
                          className="action-button action-button-blue"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="action-button action-button-red"
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={closeModal}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.5rem',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <h2 style={{ color: '#1e293b', fontSize: '1.25rem' }}>
                  {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
                </h2>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Benutzername *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Passwort {editingUser ? '(leer lassen um nicht zu ändern)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Rolle
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="user">Benutzer</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="custom-checkbox"
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontWeight: '500', color: '#374151' }}>Benutzer ist aktiv</span>
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '1.5rem'
                }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-button"
                  >
                    Abbrechen
                  </button>
                  <button type="submit" className="button-primary">
                    {editingUser ? 'Änderungen speichern' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmDialog
          show={confirmDialog.show}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
        />
      </div>
    </>
  );
};

export default Users;